import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import Title from '../../components/common/Title';
import { apiRequest, apiFormRequest } from '../../utils';
import './Finance.css';

/* One invoice: confirm what the scan read, or correct it later.

   Two screens in one, because they are the same form. Arriving from the camera
   (/finance/new) it holds a draft and a photo nothing has stored yet; arriving
   from a list (/finance/:id) it loads what was saved, with the payments already
   matched to it underneath.

   Every field is editable including the ones the scan filled in. A read that
   cannot be corrected is worse than no read: the person is holding the paper and
   can see what it actually says. */

const FIELDS = [
  ['supplier_name', 'financeSupplier', 'text'],
  ['document_number', 'financeInvoiceNumber', 'text'],
  ['reference', 'financeReference', 'text'],
  ['document_date', 'financeInvoiceDate', 'date'],
  ['due_date', 'financeDueDate', 'date'],
  ['amount_gross', 'financeAmountGross', 'decimal'],
  ['amount_vat', 'financeAmountVat', 'decimal'],
  ['amount_net', 'financeAmountNet', 'decimal'],
];

const money = (v) => (v == null ? '' : Number(v).toLocaleString('nl-BE',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const FinanceDocument = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { state } = useLocation();
  const isNew = !documentId;

  const [buId, setBuId] = useState(state?.buId || null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(() => {
    const d = state?.draft || {};
    return {
      supplier_name: d.supplier_name || '',
      supplier_vat: d.supplier_vat || '',
      document_number: d.document_number || '',
      reference: d.reference || '',
      structured_reference: d.structured_reference || '',
      document_date: d.document_date || '',
      due_date: d.due_date || '',
      // The scan returns both the printed string and its own reading; the
      // reading is what goes in the box, so a Belgian '1.512,50' does not have
      // to be retyped, and the box stays editable when it got it wrong.
      amount_gross: d.gross != null ? String(d.gross) : (d.amount_gross || ''),
      amount_vat: d.vat != null ? String(d.vat) : (d.amount_vat || ''),
      amount_net: d.net != null ? String(d.net) : (d.amount_net || ''),
      currency: d.currency || 'EUR',
      cost_category_id: '',
      note: '',
    };
  });
  const [photo] = useState(state?.photo || null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [payments, setPayments] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    apiRequest('GET', '/finance/context')
      .then((d) => {
        setCategories(d.categories || []);
        if (!buId) setBuId(d.business_unit_id || d.business_units?.[0]?.id || null);
      })
      .catch(() => setCategories([]));
  }, [buId]);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await apiRequest('GET', `/finance/documents/${documentId}`);
      const d = data.document;
      setForm({
        supplier_name: d.supplier_name || '',
        supplier_vat: d.supplier_vat || '',
        document_number: d.document_number || '',
        reference: d.reference || '',
        structured_reference: d.structured_reference || '',
        document_date: d.document_date || '',
        due_date: d.due_date || '',
        amount_gross: d.amount_gross != null ? String(d.amount_gross) : '',
        amount_vat: d.amount_vat != null ? String(d.amount_vat) : '',
        amount_net: d.amount_net != null ? String(d.amount_net) : '',
        currency: d.currency || 'EUR',
        cost_category_id: d.cost_category_id || '',
        note: d.note || '',
      });
      setPaid(d.paid);
      setPhotoUrl(data.photo_url || null);
      setPayments(data.payments || []);
    } catch {
      setError(t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }, [documentId, isNew, t]);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Reported, never enforced. Reverse charge and mixed rates make a legitimate
  // mismatch, and refusing to save over it would strand the invoice.
  const gross = parseFloat(String(form.amount_gross).replace(',', '.'));
  const vat = parseFloat(String(form.amount_vat).replace(',', '.'));
  const net = parseFloat(String(form.amount_net).replace(',', '.'));
  const vatOff = [gross, vat, net].every((n) => !Number.isNaN(n))
    && Math.abs((net + vat) - gross) > 0.01;

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) body.append(k, v); });
      body.append('bu_id', buId || '');
      body.append('confirmed', 'true');
      if (photo) body.append('photo', photo);

      const url = isNew ? '/finance/documents' : `/finance/documents/${documentId}`;
      const data = await apiFormRequest(url, body);
      setSuggested(data.suggested_payments || []);
      if (data.linked) setFlash(t('financeLinkedAutomatically', { n: data.linked }));
      if (isNew) {
        navigate(`/finance/${data.document.id}`, { replace: true });
      } else {
        setFlash((f) => f || t('saved'));
        load();
      }
    } catch (err) {
      setError(err?.message || t('somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  const link = async (paymentId) => {
    try {
      await apiRequest('POST', `/finance/documents/${documentId}/link`,
                       { payment_id: paymentId, bu_id: buId });
      setSuggested((s) => s.filter((p) => p.id !== paymentId));
      load();
    } catch (err) {
      setError(err?.message || t('somethingWentWrong'));
    }
  };

  const remove = async () => {
    if (!window.confirm(t('financeDeleteConfirm'))) return;
    try {
      await apiRequest('POST', `/finance/documents/${documentId}/delete`, { bu_id: buId });
      navigate('/finance-home', { replace: true });
    } catch (err) {
      setError(err?.message || t('somethingWentWrong'));
    }
  };

  if (loading) return <Base activeNav="home"><Loading /></Base>;

  const outstanding = (gross && paid != null) ? gross - paid : null;

  return (
    <Base activeNav="home">
      <Title my="3">{isNew ? t('financeNewInvoice') : (form.supplier_name || t('financeInvoice'))}</Title>

      {photoUrl && (
        <Card>
          {/* A short-lived signed link — the container is private, because an
              invoice carries bank details and prices. */}
          <a href={photoUrl} target="_blank" rel="noreferrer">
            <img className="fin-photo" src={photoUrl} alt={t('financeInvoice')} />
          </a>
        </Card>
      )}

      <Card>
        {FIELDS.map(([key, label, kind]) => (
          <label key={key} className="fin-label">
            {t(label)}
            <input
              className="fin-input"
              type={kind === 'date' ? 'date' : 'text'}
              inputMode={kind === 'decimal' ? 'decimal' : undefined}
              value={form[key]}
              onChange={set(key)}
            />
          </label>
        ))}

        {vatOff && <p className="fin-warn">{t('financeVatDoesNotAddUp')}</p>}

        <label className="fin-label">
          {t('financeCategory')}
          <select className="fin-input" value={form.cost_category_id}
                  onChange={set('cost_category_id')}>
            <option value="">{t('financeChooseCategory')}</option>
            {categories.map((g) => (
              g.group
                ? (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                )
                : g.options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
            ))}
          </select>
        </label>

        <label className="fin-label">
          {t('financeNote')}
          <input className="fin-input" value={form.note} onChange={set('note')} />
        </label>

        {error && <p className="fin-error">{error}</p>}
        {flash && <p className="fin-flash">{flash}</p>}

        <Button mb="0" disabled={saving} onClick={save}>
          {saving ? t('saving') : t('save')}
        </Button>
      </Card>

      {!isNew && (
        <Card>
          <div className="fin-row fin-row--head">
            <h6>{t('financePayments')}</h6>
            {outstanding != null && (
              <span className="fin-count">
                {outstanding > 0.01 ? t('financeStillOpen', { amount: money(outstanding) })
                                    : t('financeFullyPaid')}
              </span>
            )}
          </div>
          {payments.length ? payments.map((p) => (
            <div key={p.id} className="fin-doc">
              <div className="fin-doc__main">
                <span className="fin-doc__name">{p.date?.slice(0, 10)}</span>
                <span className="fin-doc__sub">{p.communication}</span>
              </div>
              <span className="fin-doc__amount">{money(p.allocated)}</span>
            </div>
          )) : <p className="fin-hint">{t('financeNoPaymentsYet')}</p>}

          {!!suggested.length && (
            <>
              <p className="fin-hint">{t('financeTheseLookLikeIt')}</p>
              {suggested.map((p) => (
                <div key={p.id} className="fin-doc">
                  <div className="fin-doc__main">
                    <span className="fin-doc__name">{p.date?.slice(0, 10)} · {money(p.amount)}</span>
                    <span className="fin-doc__sub">{p.communication}</span>
                  </div>
                  <button type="button" className="fin-link" onClick={() => link(p.id)}>
                    {t('financeLinkIt')}
                  </button>
                </div>
              ))}
            </>
          )}

          <button type="button" className="fin-delete" onClick={remove}>
            <Icon name="trash" /> {t('financeDelete')}
          </button>
        </Card>
      )}
    </Base>
  );
};

export default FinanceDocument;
